/**
 * Counter Sales (POS) API service.
 * Mirrors backend src/services/posSales.ts + routes/pos.ts.
 *   POST /pos/sales  — ring up a walk-in counter sale
 *   GET  /pos/sales  — counter sales history (WM = own, Admin = all)
 */

import { apiClient } from './api';

export type PosPaymentMethod = 'cash' | 'card' | 'ach' | 'check';

export interface PosSaleItemInput {
  sku_id: number;
  quantity: number;
  unit_price: number;
}

export interface CreatePosSaleInput {
  items: PosSaleItemInput[];
  payment_method: PosPaymentMethod;
  buyer_name?: string;
  buyer_mobile?: string;
  /** Cash tendered by the buyer. Required for cash; ignored for other methods. */
  amount_tendered?: number;
  /**
   * Manager-override audit trail (VP-POS-001). Required by the backend when
   * any line's unit_price is below the SKU's current list price.
   */
  price_override_reason?: string;
  price_override_by_user_id?: number;
}

export interface PosSaleResultItem {
  sku_id: number;
  quantity: number;
  unit_price: number;
  line_total: number;
}

export interface PosSaleResult {
  order_id: number;
  invoice_id: number;
  invoice_number: string;
  subtotal: number;
  tax_total: number;
  total_amount: number;
  payment_method: PosPaymentMethod;
  amount_tendered: number | null;
  change_due: number;
  buyer_name: string | null;
  buyer_mobile: string | null;
  items: PosSaleResultItem[];
  sold_at: string;
}

export interface PosSaleListRow {
  id: number;
  buyer_name: string | null;
  buyer_mobile: string | null;
  status: string;
  total_amount: number;
  item_count: number;
  invoice_number: string | null;
  payment_method: string | null;
  sold_by_user_id: number | null;
  sold_by: string | null;
  created_at: string;
}

export interface ListPosSalesFilters {
  from_date?: string;
  to_date?: string;
  limit?: number;
  offset?: number;
}

export interface ListPosSalesResult {
  data: PosSaleListRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface PosQuote {
  subtotal: number;
  tax_total: number;
  total_amount: number;
}

/** Price the cart (incl. warehouse-jurisdiction tax) without creating a sale. */
export async function quotePosSale(items: PosSaleItemInput[]): Promise<PosQuote> {
  return apiClient.post<PosQuote>('/pos/quote', { items });
}

export async function createPosSale(input: CreatePosSaleInput): Promise<PosSaleResult> {
  return apiClient.post<PosSaleResult>('/pos/sales', input);
}

export async function listPosSales(filters: ListPosSalesFilters = {}): Promise<ListPosSalesResult> {
  return apiClient.get<ListPosSalesResult>('/pos/sales', filters as Record<string, any>);
}

/**
 * Void a completed counter sale (VP-POS-001 manager override) — Admin-only
 * server-side. Refunds the recorded payment and cancels the order.
 */
export async function voidPosSale(orderId: number | string, reason: string): Promise<{ order_id: number; refunded: boolean }> {
  return apiClient.post(`/pos/sales/${orderId}/void`, { reason });
}

export interface PosShift {
  id: number;
  opened_by_user_id: number;
  opened_at: string;
  opening_float: number;
  closed_by_user_id: number | null;
  closed_at: string | null;
  expected_cash: number | null;
  counted_cash: number | null;
  variance: number | null;
  status: 'open' | 'closed';
  notes: string | null;
}

/** VP-POS-001 cash-drawer settlement — open a shift with a starting float. */
export async function openPosShift(openingFloat: number): Promise<PosShift> {
  return apiClient.post<PosShift>('/pos/shifts/open', { opening_float: openingFloat });
}

/** Close a shift by counting the drawer; the server computes expected vs. counted variance. */
export async function closePosShift(shiftId: number | string, countedCash: number): Promise<PosShift> {
  return apiClient.post<PosShift>(`/pos/shifts/${shiftId}/close`, { counted_cash: countedCash });
}
